<!--
@component
MatchPage

See markup below.
-->

<script lang="ts">
  import getMatchPath from '$utils/getMatchPath';
  import { Block } from '@reuters-graphics/graphics-components';

  import {
    isPlaying,
    isUpcoming,
    getVenue,
    type Match,
    type Contestant,
  } from '$utils/getMatchStats';
  import type { MatchesData } from '$lib/@types/opta';

  import CountDown from './CountDown/index.svelte';
  import MatchCard from '$lib/components/common/MatchCard/index.svelte';
  import VenueMap from './VenueMap/VenueMap.svelte';
  import Views from './Views/index.svelte';
  import { appState } from '$lib/stores/index.svelte';

  /**
   * The `data` prop is the full `appState.optaData` payload — its
   * `matches` key holds the parsed `matches.json` ({@link MatchesData}).
   */
  interface Props {
    commentary: unknown;
    match: Match | undefined;
    matchStats: Array<{ formationUsed?: unknown }> | undefined;
    homeTeam: Contestant | undefined;
    awayTeam: Contestant | undefined;
    matchUrl: string;
    data: { matches?: MatchesData } & Record<string, unknown>;
    /** URL to the photo galleries manifest, sourced from page content. */
    galleriesUrl?: string;
  }

  let {
    commentary,
    match,
    matchStats,
    homeTeam,
    awayTeam,
    matchUrl,
    data,
    galleriesUrl,
  }: Props = $props();

  const isLive = $derived(isPlaying(match));
  const isFixture = $derived(isUpcoming(match));

  // `getVenue` returns an array (0 or 1 element) — take the first or undefined.
  const venue = $derived(
    getVenue(match, data?.matches?.venues?.venue ?? [])[0]
  );

  const stage = $derived(match?.matchInfo?.stage?.name);

  // Parse coordinates once, guarding against missing / non-numeric values.
  const venueCoords = $derived.by(() => {
    if (!venue?.mapsGeoCodeLatitude || !venue?.mapsGeoCodeLongitude)
      return null;
    const lat = parseFloat(venue.mapsGeoCodeLatitude);
    const lon = parseFloat(venue.mapsGeoCodeLongitude);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  });

  const countdownDate = $derived.by(() => {
    const iso = match?.isoDate;
    return iso ? new Date(iso) : null;
  });
</script>

<!--
  Render the page shell (stage header, venue, venue map) and the hero
  `MatchCard` whenever `match` is available. The live card carries its
  own loading shimmer (`class:loading={appState.isLoading}`) for the
  data-still-settling window, so we don't need a separate placeholder
  layer here. If `match` is still resolving (cold-start deep-link) we
  render nothing for the hero — the surrounding page shell (menu,
  background, footer) keeps the page from being completely blank.
-->
{#if match}
  <Block width="normal" class="fmt-4 text-center">
    <h1 class="visually-hidden">
      {getMatchPath(match)?.label}
    </h1>
    <div class="match-stage">
      <div class="hr"></div>
      <div class="name">{stage}</div>
      <div class="hr"></div>
    </div>

    {#if venue}
      <div class="venue text-secondary text-xs fmt-0">
        {venue.name || ''}, {venue.city || ''}
      </div>
      {#if venueCoords}
        <VenueMap
          lat={venueCoords.lat}
          lon={venueCoords.lon}
          country={venue.country}
          combined={false}
        />
      {/if}
    {/if}

    <Block class="fmt-7 fmb-4">
      <MatchCard {match} flagLink={true} i={0} live={isLive} sticky={true} />
    </Block>
  </Block>

  {#if isFixture && countdownDate}
    <Block width="normal">
      <CountDown matchtime={countdownDate} />
    </Block>
  {:else if !isFixture}
    <Views
      {commentary}
      {match}
      {matchStats}
      {homeTeam}
      {awayTeam}
      {matchUrl}
      {galleriesUrl}
    />
  {/if}
{/if}

<style lang="scss">
  @use '../../styles/mixins' as mixins;

  .match-stage {
    @include mixins.match-stage;
  }
</style>
